import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDivisions, useExhibitors } from '@/api/hooks'
import { useCurrentShow } from '@/context/current-show'
import { SelectShowNotice } from '@/components/current-show'
import { ENTRY_STATUS_LABELS } from '@/lib/entry-status'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { showAware } from '@/lib/paths'
import { useShowSlug } from '@/hooks/useShowSlug'
import { formatRoleList, useActorPermissions } from '@/hooks/useActorPermissions'
import { ShowStatsSummary, useShowStatsSummary } from '@/components/show-stats-summary'

interface ClassEntriesOverviewProps {
  showId?: string
  className?: string
}

type ClassEntryRow = {
  id: string
  entryCode?: string | null
  entrantId?: string | null
  entrantName: string
  entrantFirstName?: string | null
  entrantLastName?: string | null
  clubName?: string | null
  overrideDuplicateReason?: string | null
  disqualified: boolean
}

type ClassSection = {
  id: string
  code?: string
  name: string
  divisionName?: string
  divisionOrder: number
  classOrder: number
  isBlackJar: boolean
  labelsPerExhibit: number
  entries: ClassEntryRow[]
}

const FALLBACK_DIVISION_ORDER = Number.MAX_SAFE_INTEGER
const FALLBACK_CLASS_ORDER = Number.MAX_SAFE_INTEGER

const normalizeNamePart = (value?: string | null) => value?.trim() ?? ''

const deriveSortableNameParts = (value: string) => {
  const normalized = normalizeNamePart(value)
  if (!normalized) {
    return { first: '', last: '' }
  }

  if (normalized.includes(',')) {
    const [last, ...rest] = normalized.split(',')
    const candidateLast = normalizeNamePart(last)
    const candidateFirst = normalizeNamePart(rest.join(','))
    return {
      first: candidateFirst,
      last: candidateLast || normalized,
    }
  }

  const segments = normalized.split(/\s+/)
  if (segments.length === 1) {
    return { first: '', last: normalized }
  }

  const last = segments.pop() ?? ''
  const first = segments.join(' ')
  return {
    first: normalizeNamePart(first),
    last: normalizeNamePart(last) || normalized,
  }
}

const isBlackJarClass = (
  klass?: {
    catalogClassId?: string | null
    catalogClass?: { id?: string | null; name?: string | null }
    name?: string | null
    displayName?: string | null
  } | null
): boolean => {
  if (!klass) return false
  const catalogId = klass.catalogClassId ?? klass.catalogClass?.id ?? undefined
  if (catalogId && catalogId.toLowerCase() === 'h-black-jar') return true
  const name = (klass.displayName ?? klass.name ?? klass.catalogClass?.name ?? '').toLowerCase()
  return name.includes('black jar')
}

export function ClassEntriesOverview({ showId, className }: ClassEntriesOverviewProps) {
  const { currentShow } = useCurrentShow()
  const clubsEnabled = currentShow?.enableClubs !== false
  const resolvedShowId = showId ?? currentShow?.id
  const [showEntrantNames, setShowEntrantNames] = useState(currentShow?.status === 'finalized')
  const [hasUserToggled, setHasUserToggled] = useState(false)
  const currentSlug = useShowSlug()
  const entrantCollator = useMemo(
    () => new Intl.Collator(undefined, { usage: 'sort', sensitivity: 'base' }),
    []
  )
  const hideEmptyClasses = currentShow?.status === 'judging' || currentShow?.status === 'finalized'

  useEffect(() => {
    if (currentShow?.status === 'finalized' && !hasUserToggled) {
      setShowEntrantNames(true)
    }
  }, [currentShow?.status, hasUserToggled])

  const permissions = useActorPermissions(resolvedShowId)
  // Judges cannot read the entrant list and stewards cannot read show stats; skip the reads the
  // server would refuse.
  const canReadEntrants = permissions.can('exhibitors:write')
  const canReadStats = permissions.can('reports:results')
  const divisionsQuery = useDivisions(resolvedShowId)
  const exhibitorsQuery = useExhibitors(
    { showId: resolvedShowId },
    { enabled: Boolean(resolvedShowId) && canReadEntrants }
  )
  const statsSummary = useShowStatsSummary(canReadStats ? resolvedShowId : undefined)

  const aggregated = useMemo(() => {
    const sections = new Map<string, ClassSection>()

    divisionsQuery.data?.forEach(division => {
      const divisionOrder = division.order ?? FALLBACK_DIVISION_ORDER
      const divisionName = division.name
      division.classes?.forEach(klass => {
        const classId = klass.id ?? `${division.id ?? 'division'}-${klass.code}`
        if (!sections.has(classId)) {
          sections.set(classId, {
            id: classId,
            code: klass.code,
            name: klass.displayName ?? klass.name,
            divisionName,
            divisionOrder,
            classOrder: klass.order ?? FALLBACK_CLASS_ORDER,
            isBlackJar: isBlackJarClass(klass),
            labelsPerExhibit: resolveLabelsPerExhibitFromClass(klass),
            entries: [],
          })
        }
      })
    })

    exhibitorsQuery.data?.forEach(exhibitor => {
      const entrantId = exhibitor.id ?? exhibitor.person?.id ?? null
      const entrantName = exhibitor.person?.name ?? exhibitor.name ?? 'Unknown entrant'
      const basePersonName = normalizeNamePart(exhibitor.person?.name)
      const baseExhibitorName = normalizeNamePart(exhibitor.name)
      const fallbackFullName = basePersonName || baseExhibitorName
      const derivedParts = deriveSortableNameParts(fallbackFullName)
      const resolvedFirstName = derivedParts.first
      const resolvedLastName =
        derivedParts.last || fallbackFullName || normalizeNamePart(entrantName)

      exhibitor.entries?.forEach(entry => {
        if (entry.status === 'not_entered') return
        const klass = entry.class
        const classId = klass?.id ?? klass?.code ?? `entry-${entry.id ?? entry.entryCode}`
        if (!sections.has(classId)) {
          sections.set(classId, {
            id: classId,
            code: klass?.code,
            name: klass?.name ?? 'Unassigned class',
            divisionName: undefined,
            divisionOrder: FALLBACK_DIVISION_ORDER,
            classOrder: FALLBACK_CLASS_ORDER,
            isBlackJar: isBlackJarClass({ name: klass?.name }),
            labelsPerExhibit: resolveLabelsPerExhibitFromClass(klass),
            entries: [],
          })
        }

        const target = sections.get(classId)
        if (!target) return

        const resolvedClubName = clubsEnabled ? (exhibitor.showClubName ?? null) : null

        target.entries.push({
          id: entry.id ?? `${classId}-${entry.entryCode}`,
          entryCode: entry.entryCode,
          entrantId,
          entrantName,
          entrantFirstName: resolvedFirstName || null,
          entrantLastName: resolvedLastName || null,
          clubName: resolvedClubName,
          overrideDuplicateReason: entry.overrideDuplicateReason ?? null,
          disqualified: entry.status === 'disqualified',
        })
      })
    })

    const sortedSections = Array.from(sections.values())
      .map(section => ({
        ...section,
        entries: [...section.entries].sort((a, b) => {
          const lastA = normalizeNamePart(a.entrantLastName)
          const lastB = normalizeNamePart(b.entrantLastName)
          if (lastA || lastB) {
            const compareLast = entrantCollator.compare(
              lastA || a.entrantName,
              lastB || b.entrantName
            )
            if (compareLast !== 0) return compareLast
          }
          const firstA = normalizeNamePart(a.entrantFirstName)
          const firstB = normalizeNamePart(b.entrantFirstName)
          if (firstA || firstB) {
            const compareFirst = entrantCollator.compare(
              firstA || a.entrantName,
              firstB || b.entrantName
            )
            if (compareFirst !== 0) return compareFirst
          }
          const compareName = entrantCollator.compare(a.entrantName, b.entrantName)
          if (compareName !== 0) return compareName
          const codeA = a.entryCode ?? ''
          const codeB = b.entryCode ?? ''
          const compareCode = entrantCollator.compare(codeA, codeB)
          if (compareCode !== 0) return compareCode
          return entrantCollator.compare(a.id, b.id)
        }),
      }))
      .sort((a, b) => {
        if (a.divisionOrder !== b.divisionOrder) return a.divisionOrder - b.divisionOrder
        if (a.classOrder !== b.classOrder) return a.classOrder - b.classOrder
        return a.name.localeCompare(b.name)
      })

    return {
      sections: sortedSections,
    }
  }, [divisionsQuery.data, exhibitorsQuery.data, entrantCollator, clubsEnabled])

  const classesWithEntries = useMemo(
    () => aggregated.sections.filter(section => section.entries.length > 0).length,
    [aggregated.sections]
  )

  const classesWithEntriesMetric = {
    key: 'classesWithEntries',
    label: 'Classes with entries',
    value: classesWithEntries,
  }

  const sectionsToRender = useMemo(
    () =>
      hideEmptyClasses
        ? aggregated.sections.filter(section => section.entries.length > 0)
        : aggregated.sections,
    [aggregated.sections, hideEmptyClasses]
  )

  const isLoading = permissions.isLoading || divisionsQuery.isLoading || exhibitorsQuery.isLoading
  const isError = divisionsQuery.isError || exhibitorsQuery.isError
  const error = divisionsQuery.error ?? exhibitorsQuery.error

  if (!resolvedShowId) {
    return <SelectShowNotice />
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Class Entries Overview</CardTitle>
          <CardDescription>
            Review entry rosters per class and surface quick anonymized counts for run mode.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant={showEntrantNames ? 'secondary' : 'outline'}
          onClick={() => {
            setHasUserToggled(true)
            setShowEntrantNames(value => !value)
          }}
          aria-pressed={showEntrantNames}
        >
          {showEntrantNames ? 'Hide entrant names' : 'Show entrant names'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {!isLoading && !canReadEntrants ? (
          <p className="text-sm text-muted-foreground">
            Class rosters require {formatRoleList(permissions.getAllowedRoles('exhibitors:write'))}.
          </p>
        ) : isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading class entries…
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            Unable to load entries overview.{' '}
            {error instanceof Error ? error.message : 'Try again later.'}
          </div>
        ) : (
          <>
            {statsSummary.isLoading ? (
              <div
                data-testid="class-entries-overview.stats-loading"
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading show stats…
              </div>
            ) : statsSummary.isError ? (
              <div data-testid="class-entries-overview.stats-error" className="space-y-3">
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                >
                  Unable to load show stats. Class rosters remain available.{' '}
                  {statsSummary.error instanceof Error
                    ? statsSummary.error.message
                    : 'Try again later.'}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-md border px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {classesWithEntriesMetric.label}
                    </p>
                    <p
                      className="text-2xl font-semibold"
                      data-testid="class-entries-overview.classes-with-entries"
                    >
                      {classesWithEntriesMetric.value}
                    </p>
                  </div>
                </div>
              </div>
            ) : statsSummary.summary ? (
              <ShowStatsSummary
                summary={statsSummary.summary}
                enableClubs={currentShow?.enableClubs !== false}
                variant="compact"
                className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
                additionalMetrics={[classesWithEntriesMetric]}
              />
            ) : (
              <div className="space-y-3">
                {canReadStats && (
                  <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
                    Show stats are unavailable for this show. Class totals will appear once stats
                    are published.
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-md border px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {classesWithEntriesMetric.label}
                    </p>
                    <p
                      className="text-2xl font-semibold"
                      data-testid="class-entries-overview.classes-with-entries"
                    >
                      {classesWithEntriesMetric.value}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {aggregated.sections.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No classes available for this show yet.
              </p>
            ) : sectionsToRender.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No classes with entries available for this show yet.
              </p>
            ) : (
              <div className="space-y-4">
                {sectionsToRender.map(section => (
                  <div
                    key={section.id}
                    className="overflow-hidden rounded-md border"
                    data-testid="class-entries-overview.section"
                  >
                    <div className="flex flex-col gap-1 border-b bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        {section.divisionName ? (
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {section.divisionName}
                          </p>
                        ) : null}
                        <p className="font-semibold flex flex-wrap items-center gap-2">
                          {section.code ? (
                            <span>
                              <span className="text-muted-foreground">{section.code}</span>
                              <span className="mx-2" aria-hidden="true">
                                •
                              </span>
                              {section.name}
                            </span>
                          ) : (
                            section.name
                          )}
                        </p>
                      </div>
                      <Badge variant="secondary" className="w-fit">
                        {section.entries.length}{' '}
                        {section.entries.length === 1 ? 'entry' : 'entries'}
                      </Badge>
                    </div>
                    {section.entries.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground">
                        No entries for this class yet.
                      </p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[120px]">Exhibit ID</TableHead>
                            <TableHead className="w-full">Entrant</TableHead>
                            {clubsEnabled ? (
                              <TableHead className="whitespace-nowrap">Club</TableHead>
                            ) : null}
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {section.entries.map(entry => (
                            <TableRow key={entry.id}>
                              <TableCell className="font-mono text-sm">
                                {entry.entryCode ?? '—'}
                                {entry.disqualified ? (
                                  <span className="sr-only">
                                    {' '}
                                    {ENTRY_STATUS_LABELS.disqualified}
                                  </span>
                                ) : null}
                                {entry.overrideDuplicateReason ? (
                                  <div className="mt-1">
                                    <Badge
                                      variant="outline"
                                      className="max-w-[200px] truncate border-amber-400 text-amber-900"
                                      title={entry.overrideDuplicateReason}
                                    >
                                      Override: {entry.overrideDuplicateReason}
                                    </Badge>
                                  </div>
                                ) : null}
                              </TableCell>
                              <TableCell className="w-full">
                                {showEntrantNames ? (
                                  entry.entrantId ? (
                                    <Link
                                      to={showAware(`/entrants/${entry.entrantId}`, {
                                        currentSlug,
                                      })}
                                      className="font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/50"
                                    >
                                      {entry.entrantName}
                                    </Link>
                                  ) : (
                                    entry.entrantName
                                  )
                                ) : (
                                  <span className="italic text-muted-foreground">
                                    Entrant hidden
                                  </span>
                                )}
                              </TableCell>
                              {clubsEnabled ? (
                                <TableCell className="whitespace-nowrap">
                                  {entry.clubName ?? '—'}
                                </TableCell>
                              ) : null}
                              <TableCell className="text-right">
                                {/* Wide enough for the Disqualified badge in every class, so columns line up. */}
                                <div className="flex min-w-24 flex-col items-end gap-1">
                                  <Button size="sm" variant="ghost" asChild disabled={!entry.id}>
                                    <Link
                                      to={
                                        entry.id
                                          ? showAware(`/entries/${entry.id}`, { currentSlug })
                                          : '#'
                                      }
                                    >
                                      View
                                    </Link>
                                  </Button>
                                  {/* Announced with the Exhibit ID, not as an action. */}
                                  {entry.disqualified ? (
                                    <Badge variant="destructive" aria-hidden="true">
                                      {ENTRY_STATUS_LABELS.disqualified}
                                    </Badge>
                                  ) : null}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default ClassEntriesOverview
